// Display Component for a List of Files

import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  Link
} from "@mui/material";
import CloseIcon from "@mui/icons-material/CloseSharp";

import { WpMediaPost } from "./types";
import { useFileUpload } from "@hooks/use-file-upload";
import { Loader } from "@components/loader";

type FileDisplayListProps = {
  fileIds: string[];
};

export const FileDisplayList = ({ fileIds }: FileDisplayListProps) => {
  const { wpFiles, fetchWpFiles, loading, mediaError } = useFileUpload();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState("");
  const [selectedImageTitle, setSelectedImageTitle] = useState("");

  const imageMimePrefixes = useMemo(() => ["image/"], []);
  const normalizedFileValues = useMemo(
    () => (fileIds || []).map((id) => String(id).trim()).filter(Boolean),
    [fileIds],
  );
  const numericFileIds = useMemo(
    () => normalizedFileValues.filter((id) => /^\d+$/.test(id)),
    [normalizedFileValues],
  );
  const directFileUrls = useMemo(
    () => normalizedFileValues.filter((id) => /^https?:\/\//i.test(id)),
    [normalizedFileValues],
  );

  const extractFileNameFromUrl = (url: string): string => {
    try {
      const parsed = new URL(url);
      const last = parsed.pathname.split("/").filter(Boolean).pop();
      return decodeURIComponent(last || url);
    } catch {
      return url;
    }
  };

  const isImageUrl = (url: string) => /\.(png|jpg|jpeg|gif|webp|bmp|svg)$/i.test(url);

  useEffect(() => {
    if (numericFileIds.length > 0) {
      fetchWpFiles(numericFileIds);
    }
  }, [numericFileIds, fetchWpFiles]);

  const isImageFile = (file: WpMediaPost) => {
    if (file?.mime_type) {
      return imageMimePrefixes.some((prefix) => file.mime_type.startsWith(prefix));
    }

    return /\.(png|jpg|jpeg|gif|webp|bmp|svg)$/i.test(file.source_url || "");
  };

  const openLightbox = (file: WpMediaPost) => {
    setSelectedImageUrl(file.source_url);
    setSelectedImageTitle(file.title?.rendered || "Image preview");
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setSelectedImageUrl("");
    setSelectedImageTitle("");
  };

  return (
    <>
      {loading && (<Loader message="loading files..." />)}

      {!loading && wpFiles && wpFiles.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Stack spacing={1} sx={{ alignContent: "flex-start" }}>
            {wpFiles.map((file: WpMediaPost) => (
              isImageFile(file) ? (
                <Link
                  key={file.id}
                  component="button"
                  type="button"
                  color="primary"
                  underline="hover"
                  onClick={() => openLightbox(file)}
                  sx={{ textAlign: "left" }}
                >
                  {file.title.rendered}
                </Link>
              ) : (
                <Link
                  key={file.id}
                  href={file.source_url}
                  color="primary"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {file.title.rendered}
                </Link>
              )
            ))}
          </Stack>
        </Box>
      )}

      {!loading && directFileUrls.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Stack spacing={1} sx={{ alignContent: "flex-start" }}>
            {directFileUrls.map((url) => (
              isImageUrl(url) ? (
                <Link
                  key={url}
                  component="button"
                  type="button"
                  color="primary"
                  underline="hover"
                  onClick={() => {
                    setSelectedImageUrl(url);
                    setSelectedImageTitle(extractFileNameFromUrl(url));
                    setLightboxOpen(true);
                  }}
                  sx={{ textAlign: "left" }}
                >
                  {extractFileNameFromUrl(url)}
                </Link>
              ) : (
                <Link
                  key={url}
                  href={url}
                  color="primary"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {extractFileNameFromUrl(url)}
                </Link>
              )
            ))}
          </Stack>
        </Box>
      )}

      {!loading && wpFiles && wpFiles.length === 0 && directFileUrls.length === 0 && (
        <Typography variant="body2">No files found</Typography>
      )}

      {!loading && mediaError && (
        <Typography variant="body2" color="error">
          {mediaError}
        </Typography>
      )}

      <Dialog
        open={lightboxOpen}
        onClose={closeLightbox}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          {selectedImageTitle}
          <IconButton
            aria-label="Close image preview"
            onClick={closeLightbox}
            sx={{ position: "absolute", right: 12, top: 12 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedImageUrl ? (
            <Box
              component="img"
              src={selectedImageUrl}
              alt={selectedImageTitle || "Image preview"}
              sx={{
                width: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
              }}
            />
          ) : (
            <Typography variant="body2">Unable to load image preview.</Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
